import { useSettings } from "../settings/context";
import type { Level } from "../settings/types";

/**
 * WHERE A VALUE COMES FROM.
 *
 * Four sources can supply a value. Without this indicator the organiser cannot
 * tell whether they are looking at their own override, another language's, or the
 * shared value — nor what their next click will write. It is the single addition
 * that makes the cascade usable rather than merely endured.
 *
 * Displayed strings are French: they are read by the organiser.
 */
export function Provenance({
  settingKey,
  translatable = false,
  prose = false,
}: {
  settingKey: string;
  /** Whether this setting can be filled into the other languages. */
  translatable?: boolean;
  /**
   * Whether the value is prose in some language (a text, an HTML block, the name of a
   * step or a page). Unlike `translatable`, true in EVERY language: it is what tells
   * a text not yet translated from a value shared by the whole event.
   */
  prose?: boolean;
}) {
  const {
    detail,
    scope,
    clear,
    languages,
    typos,
    missing,
    translate,
    translating,
  } = useSettings();
  const absent = translatable ? missing(settingKey) : 0;
  const r = detail(settingKey);

  const languageName = (id: number) =>
    languages.find((l) => l.id === id)?.name ?? "?";
  const typoName = (id: number | null) =>
    typos.find((t) => t.id === id)?.name ?? "?";

  /**
   * ⚠️ A TEXT NOT YET TRANSLATED IS NOT "SHARED". Outside the reference language, a
   * prose value read from the reference row is served there only because nothing has
   * been written in this language yet: announcing it "commune", like a favicon, hid
   * from the organiser what was left to translate. A colour, an image or a switch
   * keeps "commune": it really is one value for the whole event.
   */
  const untranslated =
    prose &&
    r.level === "default" &&
    scope.languageId !== scope.defaultLanguageId;

  const badge: Record<Level, string> = {
    "typo-language": `${typoName(scope.typoId)} · ${languageName(scope.languageId)}`,
    "typo-default": typoName(scope.typoId),
    language: languageName(scope.languageId),
    default: untranslated ? languageName(scope.defaultLanguageId) : "commune",
    unset: "non définie",
  };

  const sentence: Record<Level, string> = {
    "typo-language": `Surchargée pour ${typoName(scope.typoId)} en ${languageName(scope.languageId).toLowerCase()}.`,
    "typo-default": `Vient de la surcharge ${typoName(scope.typoId)} écrite en ${languageName(scope.defaultLanguageId).toLowerCase()}.`,
    language: `Vient de la version ${languageName(scope.languageId).toLowerCase()}.`,
    default: untranslated
      ? `Pas encore traduite en ${languageName(scope.languageId).toLowerCase()} : le site sert la version écrite en ${languageName(scope.defaultLanguageId).toLowerCase()}.`
      : "Valeur commune à tout l'événement.",
    unset: "Aucune valeur enregistrée à aucun niveau.",
  };

  return (
    <div className="prov" data-n={r.level}>
      <span className="prov__b" title={sentence[r.level]}>
        {badge[r.level]}
      </span>

      {translatable && (
        <button
          type="button"
          className="prov__t"
          /* Insistent while languages are still silent, quiet once they are all
             filled: the button is a reminder first and a tool second. */
          data-missing={absent > 0 ? "1" : undefined}
          disabled={translating !== null}
          title={
            absent > 0
              ? `${absent} langue${absent > 1 ? "s" : ""} sans valeur — traduire depuis cette version`
              : "Retraduire toutes les langues depuis cette version"
          }
          onClick={() => void translate(settingKey)}
        >
          {translating === settingKey ? (
            <>
              <span className="spin" aria-hidden="true" />
              Traduction
            </>
          ) : (
            `Traduire${absent > 0 ? ` (${absent})` : ""}`
          )}
        </button>
      )}

      {/* "Restore inherited" only makes sense when something was written here.
          In the reference language with no typology there is nothing underneath:
          clearing would delete the value, not reveal an inherited one. */}
      {r.isSet && (
        <button
          type="button"
          className="prov__x"
          /* Available again: `PUT /settings/fo` now takes an `unset` list, so a
             removal can be expressed at last. It was disabled here because a
             merge cannot remove a key — the override came back on the reload. */
          title="Retirer la surcharge écrite ici"
          onClick={() => clear(settingKey)}
        >
          Reprendre l’héritée
        </button>
      )}
    </div>
  );
}
